import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useOrgModules } from '@/hooks/useOrgModules';
import { Trans, useTranslation } from 'react-i18next';
import {
  BookOpenText,
  CheckCircle,
  Download,
  FileArchive,
  RotateCcw,
  TriangleAlert,
} from 'lucide-react';
import { PageLayout } from '@/components/Layout/PageLayout';
import Modal from '@/components/Common/Modal';
import { Button } from '@/components/Common/button';
import { useAuthStore } from '@/stores/authStore';
import { notify } from '@/lib/notify';
import configurationService from '@/services/configurationService';
import organizationService from '@/services/organizationService';
import { OntologySchemaForm } from '@/components/Admin/OntologySchemaForm';
import { ThesaurusForm } from '@/components/Admin/ThesaurusForm';
import { PromptsForm } from '@/components/Admin/PromptsForm';
import { ThemesForm } from '@/components/Admin/ThemesForm';
import { DomainEntityMappingForm } from '@/components/Admin/DomainEntityMappingForm';
import { EntityTypeVariationsForm } from '@/components/Admin/EntityTypeVariationsForm';
import { SourceTypesForm } from '@/components/Admin/SourceTypesForm';
import { SystemMessagesForm } from '@/components/Admin/SystemMessagesForm';
import { RagPromptsForm } from '@/components/Admin/RagPromptsForm';
import EntityExtractionPromptsForm from '@/components/Admin/EntityExtractionPromptsForm';
import ScriptPromptsForm from '@/components/Admin/ScriptPromptsForm';
import { ReferenceTextsForm } from '@/components/Admin/ReferenceTextsForm';
import { CONFIG_FILES } from '@/components/OntologyManagement/configurationData';
import { OntologyManagementTabs } from '@/components/OntologyManagement/OntologyManagementTabs';
import { InformationTab } from '@/components/OntologyManagement/InformationTab';
import { UploadConfigModal } from '@/components/OntologyManagement/UploadConfigModal';
import { IndexingSettingsCard } from '@/components/OntologyManagement/IndexingSettingsCard';
import {
  ADVANCED_ONLY_FILE_IDS,
  CONFIG_GROUPS,
  ConfigFile,
  isStructuredEditorId,
  SectorTemplate,
  StructuredEditorId,
  UNUSED_FILE_IDS,
} from '@/components/OntologyManagement/types';
import { Organization } from '@/types';
import { Alert, AlertDescription, AlertTitle } from '@/components/Common/alert';
import { LLMProvidersCard } from '@/components/OntologyManagement/LLMProvidersCard';
import type {
  LLMModelCatalogResponse,
  LLMProvider,
  LLMProviderTypeDescriptor,
  LLMRuntimeSettingsResponse,
} from '@/services/configurationService';

const CONFIG_MODE_STORAGE_KEY = 'etabli-knowledge-management-config-mode';
const ADVANCED_ONLY_SET = new Set<string>(ADVANCED_ONLY_FILE_IDS);
const UNUSED_FILE_ID_SET = new Set<string>(UNUSED_FILE_IDS);
// Files the backend never reads are dropped up front, so they reach neither the
// group tabs nor the "no configuration yet" alert.
const VISIBLE_CONFIG_FILES = CONFIG_FILES.filter((file) => !UNUSED_FILE_ID_SET.has(file.id));
const LLM_RUNTIME_FEATURES = [
  { id: 'ai_extraction', labelKey: 'knowledgeManagement.llmServices.ai_extraction', defaultLabel: 'GenieImporter' },
  { id: 'ai_filler', labelKey: 'knowledgeManagement.llmServices.ai_filler', defaultLabel: 'GenieFiller' },
  { id: 'ai_report', labelKey: 'knowledgeManagement.llmServices.ai_report', defaultLabel: 'GenieReporter' },
  { id: 'chatbots', labelKey: 'knowledgeManagement.llmServices.chatbots', defaultLabel: 'Custom agents' },
  { id: 'rag', labelKey: 'knowledgeManagement.llmServices.rag', defaultLabel: 'knowledge generation' },
  // NOTE: `embedding` is intentionally NOT in this list. The embedding model is a
  // distinct, OpenAI-only choice surfaced in the "Document indexing" card below
  // (IndexingSettingsCard) — a chat model here would be rejected by /v1/embeddings.
  { id: 'genie_artifact', labelKey: 'knowledgeManagement.llmServices.genie_artifact', defaultLabel: 'GenieArtifact' },
  { id: 'cowork_handoff', labelKey: 'knowledgeManagement.llmServices.cowork_handoff', defaultLabel: 'CoWork handoff' },
  { id: 'project_analysis', labelKey: 'knowledgeManagement.llmServices.project_analysis', defaultLabel: 'Project analysis' },
  { id: 'portfolio', labelKey: 'knowledgeManagement.llmServices.portfolio', defaultLabel: 'Portfolio map' },
] as const;
// Services whose prompts rely on LLM tool/function calling. If they are bound to
// a provider that cannot do tool calls, the feature silently degrades — warn.
const TOOL_DEPENDENT_FEATURES = new Set(['ai_extraction', 'genie_artifact', 'chatbots']);

function filterFilesForConfigMode(files: ConfigFile[], advanced: boolean): ConfigFile[] {
  if (advanced) return files;
  return files.filter((f) => !ADVANCED_ONLY_SET.has(f.id));
}

const STRUCTURED_EDITOR_META: Record<
  StructuredEditorId,
  { titleKey: string; className: string; filename: string; mimeType?: string }
> = {
  ontology_schema: {
    titleKey: 'knowledgeManagement.editorTitles.ontologySchema',
    className: 'max-w-6xl',
    filename: 'ontology_schema.yml',
  },
  thesaurus: {
    titleKey: 'knowledgeManagement.editorTitles.thesaurus',
    className: 'max-w-6xl',
    filename: 'thesaurus.json',
    mimeType: 'application/json',
  },
  prompts: {
    titleKey: 'knowledgeManagement.editorTitles.prompts',
    className: 'max-w-7xl',
    filename: 'prompts.yml',
  },
  themes: {
    titleKey: 'knowledgeManagement.editorTitles.themes',
    className: 'max-w-6xl',
    filename: 'themes.yml',
  },
  domain_entity_mapping: {
    titleKey: 'knowledgeManagement.editorTitles.domainEntityMapping',
    className: 'max-w-6xl',
    filename: 'domain_entity_mapping.yml',
  },
  entity_type_variations: {
    titleKey: 'knowledgeManagement.editorTitles.entityTypeVariations',
    className: 'max-w-6xl',
    filename: 'entity_type_variations.yml',
  },
  source_types: {
    titleKey: 'knowledgeManagement.editorTitles.sourceTypes',
    className: 'max-w-6xl',
    filename: 'source_types.yml',
  },
  system_messages: {
    titleKey: 'knowledgeManagement.editorTitles.systemMessages',
    className: 'max-w-6xl',
    filename: 'system_messages.yml',
  },
  rag_prompts: {
    titleKey: 'knowledgeManagement.editorTitles.ragPrompts',
    className: 'max-w-6xl',
    filename: 'rag_prompts.yml',
  },
  entity_extraction_prompts: {
    titleKey: 'knowledgeManagement.editorTitles.entityExtractionPrompts',
    className: 'max-w-6xl',
    filename: 'entity_extraction_prompts.yml',
  },
  script_prompts: {
    titleKey: 'knowledgeManagement.editorTitles.scriptPrompts',
    className: 'max-w-6xl',
    filename: 'script_prompts.yml',
  },
  reference_texts: {
    titleKey: 'knowledgeManagement.editorTitles.referenceTexts',
    className: 'max-w-6xl',
    filename: 'reference_texts.yml',
  },
  ontology_owl: {
    titleKey: 'knowledgeManagement.editorTitles.ontologyOwl',
    className: 'max-w-7xl',
    filename: 'ontology.owl',
    mimeType: 'application/rdf+xml',
  },
};

const belongsToGroup = <T extends readonly string[]>(group: T, fileId: string): boolean =>
  group.includes(fileId as T[number]);

export default function OntologyManagementPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const [configFiles, setConfigFiles] = useState<ConfigFile[]>(VISIBLE_CONFIG_FILES);
  const [selectedTab, setSelectedTab] = useState(0);
  const [searchParams] = useSearchParams();
  const { moduleEnabled } = useOrgModules();
  const [editingFile, setEditingFile] = useState<string | null>(null);
  const [activeStructuredEditor, setActiveStructuredEditor] = useState<StructuredEditorId | null>(null);
  const [recentlySaved, setRecentlySaved] = useState<Record<string, boolean>>({});
  const [templates, setTemplates] = useState<SectorTemplate[]>([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [resettingToSectorDefaults, setResettingToSectorDefaults] = useState(false);
  const [templateSearch, setTemplateSearch] = useState('');
  const [importingZip, setImportingZip] = useState(false);
  const zipInputRef = useRef<HTMLInputElement>(null);
  const [templateToConfirm, setTemplateToConfirm] = useState<SectorTemplate | null>(null);
  const [expandedSpecs, setExpandedSpecs] = useState<Record<string, boolean>>({});
  const [uploadModalOpen, setUploadModalOpen] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showInformationModal, setShowInformationModal] = useState(false);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [llmRuntimeSettings, setLlmRuntimeSettings] = useState<LLMRuntimeSettingsResponse | null>(null);
  const [llmModelCatalog, setLlmModelCatalog] = useState<LLMModelCatalogResponse | null>(null);
  const [llmProviders, setLlmProviders] = useState<LLMProvider[]>([]);
  const [llmProviderTypes, setLlmProviderTypes] = useState<LLMProviderTypeDescriptor[]>([]);
  const [llmSettingsLoading, setLlmSettingsLoading] = useState(false);
  const [llmSettingsSaving, setLlmSettingsSaving] = useState(false);
  const [configMode, setConfigMode] = useState<'simple' | 'advanced'>(() => {
    try {
      const stored = localStorage.getItem(CONFIG_MODE_STORAGE_KEY);
      return stored === 'advanced' ? 'advanced' : 'simple';
    } catch {
      return 'simple';
    }
  });

  const updateSavedBadge = useCallback((fileId: string) => {
    setRecentlySaved((prev) => ({ ...prev, [fileId]: true }));
    setTimeout(() => {
      setRecentlySaved((prev) => ({ ...prev, [fileId]: false }));
    }, 3000);
  }, []);

  const loadConfiguration = useCallback(async () => {
    if (!user?.organization_id) return;

    try {
      const data = await configurationService.getOrganizationConfiguration(user.organization_id);
      if (data.files) {
        setConfigFiles((prev) =>
          prev.map((file) => ({
            ...file,
            uploaded: !!data.files?.[`${file.id}_path`],
          }))
        );
      }
    } catch (error) {
      console.error('Failed to load configuration:', error);
    }
  }, [user?.organization_id]);

  const loadTemplates = useCallback(async () => {
    try {
      const data = await configurationService.getTemplates();
      if (data.templates) {
        setTemplates(data.templates);
      }
    } catch (error) {
      console.error('Failed to load templates:', error);
    }
  }, []);

  const loadOrganization = useCallback(async () => {
    try {
      const data = await organizationService.getMyOrganization();
      setOrganization(data);
    } catch (error) {
      console.error('Failed to load organization:', error);
    }
  }, []);

  const loadLlmRuntimeSettings = useCallback(async () => {
    if (!user?.organization_id) return;
    setLlmSettingsLoading(true);
    try {
      const [settingsData, catalogData, providerData, providerTypeData] = await Promise.all([
        configurationService.getLLMRuntimeSettings(user.organization_id),
        configurationService.getLLMModelCatalog(),
        configurationService.getLLMProviders(user.organization_id),
        configurationService.getLLMProviderTypes(),
      ]);
      setLlmRuntimeSettings(settingsData);
      setLlmModelCatalog(catalogData);
      setLlmProviders(providerData.providers ?? []);
      setLlmProviderTypes(providerTypeData.provider_types ?? []);
    } catch (error) {
      console.error('Failed to load llm runtime settings:', error);
    } finally {
      setLlmSettingsLoading(false);
    }
  }, [user?.organization_id]);

  useEffect(() => {
    if (!user || (user.role !== 'admin' && user.role !== 'super_admin')) {
      notify.error(
        t('knowledgeManagement.page.accessDeniedTitle'),
        t('knowledgeManagement.page.accessDeniedMessage')
      );
      navigate('/welcome');
    }
  }, [navigate, t, user]);

  useEffect(() => {
    if (user?.organization_id) {
      loadConfiguration();
      loadTemplates();
      loadOrganization();
      loadLlmRuntimeSettings();
    }
  }, [loadConfiguration, loadLlmRuntimeSettings, loadOrganization, loadTemplates, user?.organization_id]);

  useEffect(() => {
    try {
      localStorage.setItem(CONFIG_MODE_STORAGE_KEY, configMode);
    } catch {
      /* ignore */
    }
  }, [configMode]);

  useEffect(() => {
    if (searchParams.get('tab') === 'ontology') setSelectedTab(moduleEnabled('projects') ? 3 : 2);
  }, [moduleEnabled, searchParams]);

  const saveConfigContent = useCallback(
    async (fileId: string, filename: string, content: string, mimeType = 'text/plain') => {
      if (!user?.organization_id) return;

      try {
        const blob = new Blob([content], { type: mimeType });
        const file = new File([blob], filename);
        await configurationService.uploadConfigurationFile(user.organization_id, fileId, file);

        updateSavedBadge(fileId);
        await loadConfiguration();
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : t('knowledgeManagement.page.saveError');
        notify.error(t('knowledgeManagement.page.saveErrorTitle'), errorMessage);
        throw error;
      }
    },
    [loadConfiguration, t, updateSavedBadge, user?.organization_id]
  );

  const handleFileSelect = (fileId: string, selectedFile: File | null) => {
    setConfigFiles((prev) =>
      prev.map((file) =>
        file.id === fileId ? { ...file, file: selectedFile, error: null } : file
      )
    );
  };

  const handleUploadFile = async (fileId: string) => {
    const configFile = configFiles.find((file) => file.id === fileId);
    if (!configFile?.file || !user?.organization_id) {
      notify.error(t('global.error'), t('knowledgeManagement.page.selectFileRequired'));
      return;
    }

    setConfigFiles((prev) =>
      prev.map((file) => (file.id === fileId ? { ...file, loading: true, error: null } : file))
    );

    try {
      await configurationService.uploadConfigurationFile(user.organization_id, fileId, configFile.file);

      setConfigFiles((prev) =>
        prev.map((file) =>
          file.id === fileId
            ? { ...file, loading: false, uploaded: true, file: null, error: null }
            : file
        )
      );

      updateSavedBadge(fileId);
      await loadConfiguration();
    } catch (error) {
      let notifyMessage = t('knowledgeManagement.page.uploadError');
      let cardError: string | null = null;

      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { status?: number; data?: { detail?: unknown } } };

        if (axiosError.response?.status === 400 && axiosError.response.data?.detail) {
          const detail = axiosError.response.data.detail;

          if (
            typeof detail === 'object' &&
            detail !== null &&
            'error_type' in detail &&
            detail.error_type === 'validation_error' &&
            'files' in detail
          ) {
            const validationError = detail as {
              message?: string;
              files: Array<{ filename: string; error: string }>;
            };
            const fileErrors = validationError.files || [];

            if (fileErrors.length > 0) {
              const formattedErrors = fileErrors
                .map(({ filename, error: fileError }) => {
                  const formatted = fileError.replace(
                    /Missing required keys: ([a-zA-Z_,\s]+)/g,
                    (_match, keys) => {
                      const keyList = keys
                        .split(',')
                        .map((key: string) => `\`${key.trim()}\``)
                        .join(', ');
                      return `Missing required keys: ${keyList}`;
                    }
                  );

                  return `- ${filename}: ${formatted}`;
                })
                .join('\n');

              const thisFileError = fileErrors.find(
                ({ filename }) => filename === configFile.filename
              );

              if (thisFileError) {
                cardError = thisFileError.error.replace(
                  /Missing required keys: ([a-zA-Z_,\s]+)/g,
                  (_match, keys) => {
                    const keyList = keys
                      .split(',')
                      .map((key: string) => `\`${key.trim()}\``)
                      .join(', ');
                    return `Missing required keys: ${keyList}`;
                  }
                );
              }

              notifyMessage = t('knowledgeManagement.page.fileNotValid', { errors: formattedErrors });
            } else {
              notifyMessage = validationError.message || notifyMessage;
              cardError = notifyMessage;
            }
          } else if (typeof detail === 'string') {
            notifyMessage = detail;
            cardError = detail;
          }
        } else if (axiosError.response?.data?.detail) {
          const detail = axiosError.response.data.detail;
          notifyMessage = typeof detail === 'string' ? detail : JSON.stringify(detail);
          cardError = notifyMessage;
        }
      }

      setConfigFiles((prev) =>
        prev.map((file) => (file.id === fileId ? { ...file, loading: false, error: cardError } : file))
      );

      notify.error(t('knowledgeManagement.page.uploadErrorTitle'), notifyMessage);
    }
  };

  const handleViewFile = async (fileId: string) => {
    if (fileId === 'ontology_owl') {
      navigate('/admin/configuration/ontology/owl');
      return;
    }
    if (!user?.organization_id) return;

    try {
      const content = await configurationService.getConfigurationFileContent(user.organization_id, fileId);
      setConfigFiles((prev) =>
        prev.map((file) => (file.id === fileId ? { ...file, content } : file))
      );

      if (isStructuredEditorId(fileId)) {
        setActiveStructuredEditor(fileId);
      } else {
        setEditingFile(fileId);
      }
    } catch {
      notify.error(t('global.error'), t('knowledgeManagement.page.loadFileError'));
    }
  };

  const handleDownloadFile = async (fileId: string) => {
    if (!user?.organization_id) return;

    const configFile = configFiles.find((file) => file.id === fileId);
    if (!configFile) return;

    try {
      const content = await configurationService.getConfigurationFileContent(user.organization_id, fileId);
      const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = configFile.filename;
      link.click();
      URL.revokeObjectURL(blobUrl);
    } catch {
      notify.error(t('global.error'), t('knowledgeManagement.page.downloadFileError'));
    }
  };

  const handleSaveRawContent = async (fileId: string) => {
    const configFile = configFiles.find((file) => file.id === fileId);
    if (!configFile?.content) return;

    await saveConfigContent(fileId, configFile.filename, configFile.content);
    setEditingFile(null);
  };

  const handleContentChange = (fileId: string, newContent: string) => {
    setConfigFiles((prev) =>
      prev.map((file) => (file.id === fileId ? { ...file, content: newContent } : file))
    );
  };

  const toggleSpecs = (fileId: string) => {
    setExpandedSpecs((prev) => ({ ...prev, [fileId]: !prev[fileId] }));
  };

  const closeUploadModal = () => {
    setUploadModalOpen(null);
    setShowConfirmation(false);
  };

  const handleContinueUpload = (fileId: string) => {
    const configFile = configFiles.find((file) => file.id === fileId);
    if (!configFile?.file || configFile.loading) return;

    if (configFile.uploaded) {
      setShowConfirmation(true);
      return;
    }

    void handleConfirmUpload(fileId);
  };

  const handleConfirmUpload = async (fileId: string) => {
    setShowConfirmation(false);
    await handleUploadFile(fileId);
    setUploadModalOpen(null);
  };

  const handleLoadTemplate = async (templateId: string) => {
    if (!user?.organization_id) return;

    setLoadingTemplate(true);
    try {
      const data = await configurationService.loadTemplate(user.organization_id, templateId);
      notify.success(
        t('knowledgeManagement.page.templateLoadedTitle'),
        t('knowledgeManagement.page.templateLoadedDescription', { count: data.file_count })
      );
      setShowTemplateModal(false);
      await loadConfiguration();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : t('knowledgeManagement.page.templateLoadError');
      notify.error(t('global.error'), errorMessage);
    } finally {
      setLoadingTemplate(false);
    }
  };

  const handleResetToOrganizationSector = useCallback(async () => {
    if (!user?.organization_id || !organization?.sector_template_id) {
      notify.error(
        t('knowledgeManagement.resetDefaultMissingSectorTitle', 'Secteur non défini'),
        t(
          'knowledgeManagement.resetDefaultMissingSectorDescription',
          "Aucun secteur n'est défini pour cette organisation."
        )
      );
      return;
    }

    const confirmed = window.confirm(
      t(
        'knowledgeManagement.resetDefaultConfirm',
        'Cette action va réinitialiser tous les fichiers de configuration avec ceux par défaut du secteur de votre organisation. Continuer ?'
      )
    );
    if (!confirmed) return;

    setResettingToSectorDefaults(true);
    try {
      const response = await configurationService.loadTemplate(
        user.organization_id,
        organization.sector_template_id
      );
      notify.success(
        t('knowledgeManagement.resetDefaultSuccessTitle', 'Configuration réinitialisée'),
        t('knowledgeManagement.resetDefaultSuccessDescription', {
          defaultValue: '{{count}} fichiers réinitialisés depuis le secteur de l’organisation.',
          count: response.file_count,
        })
      );
      await loadConfiguration();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : t('knowledgeManagement.resetDefaultError', 'Erreur lors de la réinitialisation');
      notify.error(
        t('knowledgeManagement.resetDefaultErrorTitle', 'Réinitialisation impossible'),
        errorMessage
      );
    } finally {
      setResettingToSectorDefaults(false);
    }
  }, [loadConfiguration, organization?.sector_template_id, t, user?.organization_id]);

  const handleImportZip = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const archive = event.target.files?.[0];
      // Reset so picking the same file again still fires onChange.
      event.target.value = '';
      if (!archive || !user?.organization_id) return;

      const confirmed = window.confirm(
        t(
          'knowledgeManagement.importZipConfirm',
          'Les 13 fichiers de configuration actuels seront remplacés par ceux du zip. Continuer ?'
        )
      );
      if (!confirmed) return;

      setImportingZip(true);
      try {
        const response = await configurationService.importConfigurationZip(user.organization_id, archive);
        notify.success(
          t('knowledgeManagement.importZipSuccessTitle', 'Configuration importée'),
          t('knowledgeManagement.importZipSuccessDescription', {
            defaultValue: '{{count}} fichiers importés depuis le zip.',
            count: response.files_uploaded.length,
          })
        );
        await loadConfiguration();
      } catch (error) {
        let message = t('knowledgeManagement.importZipError', "Erreur lors de l'import du zip");
        const detail =
          error && typeof error === 'object' && 'response' in error
            ? (error as { response?: { data?: { detail?: unknown } } }).response?.data?.detail
            : undefined;
        if (typeof detail === 'string') {
          message = detail;
        } else if (detail && typeof detail === 'object') {
          const structured = detail as {
            missing?: string[];
            files?: Array<{ filename: string; error: string }>;
          };
          if (structured.missing?.length) {
            message = t('knowledgeManagement.importZipMissing', {
              defaultValue: 'Fichiers manquants : {{files}}',
              files: structured.missing.join(', '),
            });
          } else if (structured.files?.length) {
            message = structured.files.map(({ filename, error: fileError }) => `- ${filename}: ${fileError}`).join('\n');
          }
        }
        notify.error(t('knowledgeManagement.importZipErrorTitle', 'Import impossible'), message);
      } finally {
        setImportingZip(false);
      }
    },
    [loadConfiguration, t, user?.organization_id]
  );

  const filteredTemplates = useMemo(() => {
    const normalizedSearch = templateSearch.trim().toLowerCase();
    if (!normalizedSearch) return templates;

    return templates.filter((template) => {
      const haystack = `${template.name} ${template.description}`.toLowerCase();
      return haystack.includes(normalizedSearch);
    });
  }, [templateSearch, templates]);

  const groupedFiles = useMemo(() => {
    const advanced = configMode === 'advanced';
    return {
      ontologie: filterFilesForConfigMode(
        configFiles.filter((file) => belongsToGroup(CONFIG_GROUPS.ontologie, file.id)),
        advanced
      ),
      themes: filterFilesForConfigMode(
        configFiles.filter((file) => belongsToGroup(CONFIG_GROUPS.themes, file.id)),
        advanced
      ),
      thesaurus: filterFilesForConfigMode(
        configFiles.filter((file) => belongsToGroup(CONFIG_GROUPS.thesaurus, file.id)),
        advanced
      ),
      prompts: filterFilesForConfigMode(
        configFiles.filter((file) => belongsToGroup(CONFIG_GROUPS.prompts, file.id)),
        advanced
      ),
      documents: filterFilesForConfigMode(
        configFiles.filter((file) => belongsToGroup(CONFIG_GROUPS.documents, file.id)),
        advanced
      ),
    };
  }, [configFiles, configMode]);

  const hasAnyUploadedFile = useMemo(() => configFiles.some((f) => f.uploaded), [configFiles]);

  const getProviderModels = useCallback(
    (provider: string | null | undefined) => {
      if (!provider || !llmModelCatalog?.providers?.[provider]) return [];
      return llmModelCatalog.providers[provider].models;
    },
    [llmModelCatalog]
  );

  /**
   * Models a service can pick, given the provider row it is bound to.
   * Falls back to the legacy inline provider type for entries written before
   * providers became first-class rows.
   */
  const getModelsForBinding = useCallback(
    (entry: { provider_id?: string | null; provider?: string | null } | null | undefined) => {
      const bound = llmProviders.find((provider) => provider.id === entry?.provider_id);
      return getProviderModels(bound?.provider_type ?? entry?.provider);
    },
    [getProviderModels, llmProviders]
  );

  /** True when the bound provider cannot do tool calling (tool-dependent services break). */
  const bindingLacksToolSupport = useCallback(
    (entry: { provider_id?: string | null; provider?: string | null } | null | undefined) => {
      const bound = llmProviders.find((provider) => provider.id === entry?.provider_id);
      const providerType = bound?.provider_type ?? entry?.provider;
      if (!providerType) return false;
      const descriptor = llmProviderTypes.find((type) => type.provider_type === providerType);
      return descriptor ? !descriptor.supports_tools : false;
    },
    [llmProviderTypes, llmProviders]
  );

  const updateLlmDefaults = useCallback(
    (changes: Partial<LLMRuntimeSettingsResponse['defaults']>) => {
      setLlmRuntimeSettings((prev) => {
        if (!prev) return prev;
        return { ...prev, defaults: { ...prev.defaults, ...changes } };
      });
    },
    []
  );

  const updateLlmFeature = useCallback(
    (featureId: string, changes: Partial<LLMRuntimeSettingsResponse['defaults']>) => {
      setLlmRuntimeSettings((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          features: {
            ...prev.features,
            [featureId]: {
              ...prev.features[featureId],
              ...changes,
            },
          },
        };
      });
    },
    []
  );

  const handleSaveLlmRuntimeSettings = useCallback(async () => {
    if (!user?.organization_id || !llmRuntimeSettings) return;

    setLlmSettingsSaving(true);
    try {
      // Send provider_id when the service is bound to a configured provider;
      // otherwise keep sending the legacy inline provider type so orgs that
      // have not created provider rows yet keep working unchanged.
      const bindingPatch = (entry: LLMRuntimeSettingsResponse['defaults'] | undefined) =>
        entry?.provider_id
          ? { provider_id: entry.provider_id }
          : { provider: entry?.provider || undefined };

      const payload = {
        defaults: {
          ...bindingPatch(llmRuntimeSettings.defaults),
          model: llmRuntimeSettings.defaults.model || undefined,
          max_tokens:
            typeof llmRuntimeSettings.defaults.max_tokens === 'number'
              ? llmRuntimeSettings.defaults.max_tokens
              : undefined,
        },
        features: Object.fromEntries(
          LLM_RUNTIME_FEATURES.map(({ id }) => {
            const entry = llmRuntimeSettings.features[id];
            return [
              id,
              {
                ...bindingPatch(entry),
                model: entry?.model || undefined,
                max_tokens: typeof entry?.max_tokens === 'number' ? entry.max_tokens : undefined,
              },
            ];
          })
        ),
      };
      const updated = await configurationService.updateLLMRuntimeSettings(user.organization_id, payload);
      setLlmRuntimeSettings(updated);
      notify.success(
        t('knowledgeManagement.llmSettings.savedTitle'),
        t('knowledgeManagement.llmSettings.savedMessage')
      );
    } catch (error) {
      const message =
        error instanceof Error ? error.message : t('knowledgeManagement.llmSettings.saveError');
      notify.error(t('global.error'), message);
    } finally {
      setLlmSettingsSaving(false);
    }
  }, [llmRuntimeSettings, t, user?.organization_id]);

  const getFileContent = useCallback(
    (fileId: string) => configFiles.find((file) => file.id === fileId)?.content || '',
    [configFiles]
  );

  const providersTab = useMemo(
    () => (
      <LLMProvidersCard organizationId={user?.organization_id} onProvidersChanged={setLlmProviders} />
    ),
    [user?.organization_id]
  );

  const servicesTab = useMemo(() => {
    return (
      <div className="space-y-4">
        <div className="rounded-lg border border-border bg-card p-4 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="text-base font-semibold">{t('knowledgeManagement.llmSettings.title')}</h3>
              <p className="text-xs text-muted-foreground">
                {t('knowledgeManagement.llmSettings.description')}
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => void handleSaveLlmRuntimeSettings()}
              disabled={llmSettingsLoading || llmSettingsSaving || !llmRuntimeSettings}
            >
              {llmSettingsSaving ? t('actions.saving') : t('actions.save')}
            </Button>
          </div>

          {llmSettingsLoading || !llmRuntimeSettings ? (
            <p className="text-sm text-muted-foreground">{t('knowledgeManagement.llmSettings.loading')}</p>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-12 gap-2 text-xs font-medium text-muted-foreground">
                <div className="col-span-3">{t('knowledgeManagement.llmSettings.columns.service')}</div>
                <div className="col-span-2">{t('knowledgeManagement.llmSettings.columns.engine')}</div>
                <div className="col-span-4">{t('knowledgeManagement.llmSettings.columns.model')}</div>
                <div className="col-span-3">{t('knowledgeManagement.llmSettings.columns.maxTokens')}</div>
              </div>

              <div className="grid grid-cols-12 gap-2 items-center">
                <div className="col-span-3 text-sm font-medium">{t('knowledgeManagement.llmSettings.defaultRow')}</div>
                <div className="col-span-2">
                  <select
                    className="w-full rounded-md border border-border bg-background px-2 py-1 text-sm"
                    value={llmRuntimeSettings.defaults.provider_id || ''}
                    onChange={(event) => {
                      const providerId = event.target.value;
                      const bound = llmProviders.find((provider) => provider.id === providerId);
                      const models = getProviderModels(bound?.provider_type);
                      updateLlmDefaults({
                        provider_id: providerId || null,
                        provider: bound?.provider_type ?? null,
                        model: models[0]?.id ?? null,
                        max_tokens: models[0]?.default_max_tokens ?? null,
                      });
                    }}
                  >
                    <option value="">--</option>
                    {llmProviders.map((provider) => (
                      <option key={provider.id} value={provider.id}>
                        {provider.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-span-4">
                  <select
                    className="w-full rounded-md border border-border bg-background px-2 py-1 text-sm"
                    value={llmRuntimeSettings.defaults.model || ''}
                    onChange={(event) => {
                      const modelId = event.target.value;
                      const models = getModelsForBinding(llmRuntimeSettings.defaults);
                      const selected = models.find((m) => m.id === modelId);
                      updateLlmDefaults({
                        model: modelId || null,
                        max_tokens: selected?.default_max_tokens ?? llmRuntimeSettings.defaults.max_tokens,
                      });
                    }}
                  >
                    <option value="">--</option>
                    {getModelsForBinding(llmRuntimeSettings.defaults).map((model) => (
                      <option key={model.id} value={model.id}>
                        {model.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-span-3">
                  <input
                    type="number"
                    min={1}
                    className="w-full rounded-md border border-border bg-background px-2 py-1 text-sm"
                    value={llmRuntimeSettings.defaults.max_tokens ?? ''}
                    onChange={(event) =>
                      updateLlmDefaults({
                        max_tokens: event.target.value ? Number(event.target.value) : null,
                      })
                    }
                  />
                </div>
              </div>

              {LLM_RUNTIME_FEATURES.map((feature) => {
                const featureSettings = llmRuntimeSettings.features[feature.id];
                return (
                  <div key={feature.id} className="grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-3 text-sm">
                      {t(feature.labelKey, feature.defaultLabel)}
                      {TOOL_DEPENDENT_FEATURES.has(feature.id) &&
                      bindingLacksToolSupport(featureSettings) ? (
                        <span
                          className="ml-2 text-xs text-amber-600"
                          title={t(
                            'knowledgeManagement.llmProviders.noToolsWarning',
                            "Ce fournisseur ne gère pas les appels d'outils : ce service risque de mal fonctionner."
                          )}
                        >
                          ⚠
                        </span>
                      ) : null}
                    </div>
                    <div className="col-span-2">
                      <select
                        className="w-full rounded-md border border-border bg-background px-2 py-1 text-sm"
                        value={featureSettings?.provider_id || ''}
                        onChange={(event) => {
                          const providerId = event.target.value;
                          const bound = llmProviders.find((provider) => provider.id === providerId);
                          const models = getProviderModels(bound?.provider_type);
                          updateLlmFeature(feature.id, {
                            provider_id: providerId || null,
                            provider: bound?.provider_type ?? null,
                            model: models[0]?.id ?? null,
                            max_tokens: models[0]?.default_max_tokens ?? null,
                          });
                        }}
                      >
                        <option value="">--</option>
                        {llmProviders.map((provider) => (
                          <option key={provider.id} value={provider.id}>
                            {provider.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-span-4">
                      <select
                        className="w-full rounded-md border border-border bg-background px-2 py-1 text-sm"
                        value={featureSettings?.model || ''}
                        onChange={(event) => {
                          const modelId = event.target.value;
                          const models = getModelsForBinding(featureSettings);
                          const selected = models.find((m) => m.id === modelId);
                          updateLlmFeature(feature.id, {
                            model: modelId || null,
                            max_tokens: selected?.default_max_tokens ?? featureSettings?.max_tokens ?? null,
                          });
                        }}
                      >
                        <option value="">--</option>
                        {getModelsForBinding(featureSettings).map((model) => (
                          <option key={model.id} value={model.id}>
                            {model.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-span-3">
                      <input
                        type="number"
                        min={1}
                        className="w-full rounded-md border border-border bg-background px-2 py-1 text-sm"
                        value={featureSettings?.max_tokens ?? ''}
                        onChange={(event) =>
                          updateLlmFeature(feature.id, {
                            max_tokens: event.target.value ? Number(event.target.value) : null,
                          })
                        }
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <IndexingSettingsCard organizationId={user?.organization_id} />
      </div>
    );
  }, [
    bindingLacksToolSupport,
    getModelsForBinding,
    getProviderModels,
    handleSaveLlmRuntimeSettings,
    llmProviders,
    llmRuntimeSettings,
    llmSettingsLoading,
    llmSettingsSaving,
    t,
    updateLlmDefaults,
    updateLlmFeature,
    user?.organization_id,
  ]);

  // Mode switch + template actions: they only affect ontology files, so they live in
  // that tab's toolbar rather than the page header.
  const ontologyToolbar = useMemo(
    () => (
      <>
        <div className="flex rounded-lg border border-border p-0.5 bg-muted/40">
          <Button
            type="button"
            variant={configMode === 'simple' ? 'default' : 'ghost'}
            size="sm"
            className="rounded-md"
            onClick={() => setConfigMode('simple')}
          >
            {t('knowledgeManagement.modeSimple')}
          </Button>
          <Button
            type="button"
            variant={configMode === 'advanced' ? 'default' : 'ghost'}
            size="sm"
            className="rounded-md"
            onClick={() => setConfigMode('advanced')}
          >
            {t('knowledgeManagement.modeAdvanced')}
          </Button>
        </div>
        <Button onClick={() => setShowTemplateModal(true)} variant="outline" size="sm">
          <Download className="h-4 w-4 mr-2" />
          {t('knowledgeManagement.loadTemplate')}
        </Button>
        <Button
          onClick={() => zipInputRef.current?.click()}
          variant="outline"
          size="sm"
          disabled={importingZip}
          title={t('knowledgeManagement.importZipHint', 'Zip contenant les 13 fichiers de configuration')}
        >
          <FileArchive className="h-4 w-4 mr-2" />
          {importingZip
            ? t('knowledgeManagement.importingZip', 'Import en cours...')
            : t('knowledgeManagement.importZip', 'Importer un zip')}
        </Button>
        <input
          ref={zipInputRef}
          type="file"
          accept=".zip,application/zip"
          className="hidden"
          onChange={handleImportZip}
        />
        <Button
          onClick={handleResetToOrganizationSector}
          variant="outline"
          size="sm"
          disabled={resettingToSectorDefaults || !organization?.sector_template_id}
        >
          <RotateCcw className="h-4 w-4 mr-2" />
          {resettingToSectorDefaults
            ? t('knowledgeManagement.resettingDefaults', 'Réinitialisation...')
            : t('knowledgeManagement.resetToSectorDefaults', 'Réinitialiser au secteur')}
        </Button>
      </>
    ),
    [
      configMode,
      handleImportZip,
      handleResetToOrganizationSector,
      importingZip,
      organization?.sector_template_id,
      resettingToSectorDefaults,
      t,
    ]
  );

  const saveStructuredContent = useCallback(
    async (editorId: StructuredEditorId, content: string) => {
      const metadata = STRUCTURED_EDITOR_META[editorId];
      await saveConfigContent(editorId, metadata.filename, content, metadata.mimeType);
      setActiveStructuredEditor(null);
    },
    [saveConfigContent]
  );

  const renderStructuredEditorModal = () => {
    if (!activeStructuredEditor) return null;

    const metadata = STRUCTURED_EDITOR_META[activeStructuredEditor];
    const content = getFileContent(activeStructuredEditor);
    const onClose = () => setActiveStructuredEditor(null);

    const renderEditor = () => {
      switch (activeStructuredEditor) {
        case 'ontology_schema':
          return (
            <OntologySchemaForm
              initialContent={content}
              onSave={(newContent) => saveStructuredContent('ontology_schema', newContent)}
              onCancel={onClose}
            />
          );
        case 'thesaurus':
          return (
            <ThesaurusForm
              initialContent={content}
              onSave={(newContent) => saveStructuredContent('thesaurus', newContent)}
              onCancel={onClose}
            />
          );
        case 'prompts':
          return (
            <PromptsForm
              initialContent={content}
              onSave={(newContent) => saveStructuredContent('prompts', newContent)}
              onCancel={onClose}
            />
          );
        case 'themes':
          return (
            <ThemesForm
              initialContent={content}
              onSave={(newContent) => saveStructuredContent('themes', newContent)}
              onCancel={onClose}
            />
          );
        case 'domain_entity_mapping':
          return (
            <DomainEntityMappingForm
              initialContent={content}
              onSave={(newContent) => saveStructuredContent('domain_entity_mapping', newContent)}
              onCancel={onClose}
            />
          );
        case 'entity_type_variations':
          return (
            <EntityTypeVariationsForm
              initialContent={content}
              onSave={(newContent) => saveStructuredContent('entity_type_variations', newContent)}
              onCancel={onClose}
            />
          );
        case 'source_types':
          return (
            <SourceTypesForm
              initialContent={content}
              onSave={(newContent) => saveStructuredContent('source_types', newContent)}
              onCancel={onClose}
            />
          );
        case 'system_messages':
          return (
            <SystemMessagesForm
              initialContent={content}
              onSave={(newContent) => saveStructuredContent('system_messages', newContent)}
              onCancel={onClose}
            />
          );
        case 'rag_prompts':
          return (
            <RagPromptsForm
              initialContent={content}
              onSave={(newContent) => saveStructuredContent('rag_prompts', newContent)}
              onCancel={onClose}
            />
          );
        case 'entity_extraction_prompts':
          return (
            <EntityExtractionPromptsForm
              content={content}
              onSave={(newContent) => saveStructuredContent('entity_extraction_prompts', newContent)}
              onClose={onClose}
            />
          );
        case 'script_prompts':
          return (
            <ScriptPromptsForm
              content={content}
              onSave={(newContent) => saveStructuredContent('script_prompts', newContent)}
              onClose={onClose}
            />
          );
        case 'reference_texts':
          return (
            <ReferenceTextsForm
              initialContent={content}
              onSave={(newContent) => saveStructuredContent('reference_texts', newContent)}
              onCancel={onClose}
            />
          );
        default:
          return null;
      }
    };

    return (
      <Modal
        open
        title={t(metadata.titleKey)}
        onClose={onClose}
        className={metadata.className}
        hideDefaultFooter
      >
        {renderEditor()}
      </Modal>
    );
  };

  return (
    <PageLayout
      icon={<BookOpenText className="h-6 w-6" />}
      title={t('sidebar.knowledge.knowledgeManagement')}
      subtitle={t('knowledgeManagement.pageSubtitle')}
    >
      <div className="space-y-6">
        {!hasAnyUploadedFile && (
          <Alert variant="warning">
            <TriangleAlert className="h-4 w-4" />
            <AlertTitle>{t('knowledgeManagement.emptyConfigTitle')}</AlertTitle>
            <AlertDescription>{t('knowledgeManagement.emptyConfigDescription')}</AlertDescription>
          </Alert>
        )}

        <OntologyManagementTabs
          selectedTab={selectedTab}
          onTabChange={setSelectedTab}
          groupedFiles={groupedFiles}
          expandedSpecs={expandedSpecs}
          recentlySaved={recentlySaved}
          onToggleSpecs={toggleSpecs}
          onViewFile={handleViewFile}
          onDownloadFile={handleDownloadFile}
          onUploadClick={setUploadModalOpen}
          isStructuredEditor={isStructuredEditorId}
          providersTab={providersTab}
          servicesTab={servicesTab}
          ontologyToolbar={ontologyToolbar}
          onOpenInformation={() => setShowInformationModal(true)}
        />
      </div>

      {showInformationModal && (
        <Modal
          open
          title={t('knowledgeManagement.informationTitle', 'Pourquoi configurer votre assistant ?')}
          onClose={() => setShowInformationModal(false)}
          className="max-w-4xl"
          hideDefaultFooter
        >
          <InformationTab templates={templates} />
        </Modal>
      )}

      {editingFile && (
        <Modal
          open
          title={t('knowledgeManagement.page.editFileTitle', {
            filename: configFiles.find((file) => file.id === editingFile)?.filename,
          })}
          onClose={() => setEditingFile(null)}
          className="max-w-4xl"
          hideDefaultFooter
        >
          <div className="space-y-4">
            <textarea
              value={getFileContent(editingFile)}
              onChange={(e) => handleContentChange(editingFile, e.target.value)}
              className="w-full h-96 px-3 py-2 text-sm font-mono bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
              placeholder={t('global.loading')}
            />
            <div className="flex gap-2 justify-end">
              <Button onClick={() => setEditingFile(null)} variant="outline">
                {t('actions.cancel')}
              </Button>
              <Button onClick={() => void handleSaveRawContent(editingFile)}>
                <CheckCircle className="h-4 w-4 mr-2" />
                {t('actions.save')}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {renderStructuredEditorModal()}

      <UploadConfigModal
        openFileId={uploadModalOpen}
        configFiles={configFiles}
        showConfirmation={showConfirmation}
        onClose={closeUploadModal}
        onSelectFile={handleFileSelect}
        onContinue={handleContinueUpload}
        onConfirmReplace={(fileId) => void handleConfirmUpload(fileId)}
        onBackToSelection={() => setShowConfirmation(false)}
      />

      <Modal
        open={showTemplateModal}
        title={t('knowledgeManagement.loadTemplate')}
        onClose={() => {
          setShowTemplateModal(false);
          setTemplateSearch('');
        }}
        className="max-w-2xl"
        hideDefaultFooter
      >
        <div className="space-y-4">
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-foreground">
              {t('knowledgeManagement.templateModal.selectSector')}
            </h4>
            <input
              type="text"
              value={templateSearch}
              onChange={(e) => setTemplateSearch(e.target.value)}
              placeholder={t('knowledgeManagement.templateModal.searchPlaceholder')}
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-sm"
            />
            {templates.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('knowledgeManagement.templateModal.noTemplates')}</p>
            ) : filteredTemplates.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('knowledgeManagement.templateModal.noMatch')}</p>
            ) : (
              <div className="grid grid-cols-1 gap-3 max-h-[60vh] overflow-y-auto pr-2">
                {filteredTemplates.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => setTemplateToConfirm(template)}
                    disabled={loadingTemplate}
                    className="text-left p-4 rounded-lg border border-border hover:border-primary hover:bg-muted/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <h5 className="font-semibold text-foreground mb-1">{template.name}</h5>
                    <p className="text-sm text-foreground/70">{template.description}</p>
                    <p className="text-xs text-muted-foreground mt-2">
                      {t('knowledgeManagement.templateModal.fileCount', { count: template.file_count })}
                    </p>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between gap-3 pt-4 border-t border-border">
            <p className="text-xs text-amber-700 dark:text-amber-300 flex items-start gap-1.5">
              <TriangleAlert className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
              {t('knowledgeManagement.templateModal.replaceWarning')}
            </p>
            <Button onClick={() => setShowTemplateModal(false)} variant="outline" disabled={loadingTemplate}>
              {t('actions.cancel')}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={!!templateToConfirm}
        title={t('knowledgeManagement.templateConfirm.title')}
        onClose={() => setTemplateToConfirm(null)}
        className="max-w-lg"
        hideDefaultFooter
      >
        <div className="space-y-4">
          <p className="text-sm text-foreground/90">
            <Trans
              i18nKey="knowledgeManagement.templateConfirm.message"
              values={{ name: templateToConfirm?.name ?? '' }}
              components={{ strong: <strong /> }}
            />
          </p>
          <p className="text-sm text-muted-foreground">
            {t('knowledgeManagement.templateConfirm.warning')}
          </p>
          <div className="flex gap-2 justify-end pt-2 border-t border-border">
            <Button
              onClick={() => setTemplateToConfirm(null)}
              variant="outline"
              disabled={loadingTemplate}
            >
              {t('actions.cancel')}
            </Button>
            <Button
              onClick={async () => {
                if (!templateToConfirm) return;
                const templateId = templateToConfirm.id;
                setTemplateToConfirm(null);
                await handleLoadTemplate(templateId);
              }}
              disabled={loadingTemplate}
            >
              {loadingTemplate ? t('global.loading') : t('global.confirm')}
            </Button>
          </div>
        </div>
      </Modal>
    </PageLayout>
  );
}
